/**
 * Copy deck for the restaurant profile's TasteMatch™ explainer modal
 * (`CompatibilityModal`).
 *
 * The modal body is the legacy "Compatibility Key" from the old Ionic app
 * (`courseapp-client/src/assets/info.json`, `home` key), kept to its original
 * wording and re-branded for TasteMatch: six tier rows, then two
 * explanatory paragraphs. Wording changes from the legacy text, and why:
 *
 * - Row labels: "<Tier> Personal Compatibility" became "<Tier> Personal
 *   TasteMatch" ("Personal TasteMatch" is the sanctioned two-word qualified
 *   usage; the fused "PersonalTasteMatch™" is wrong). No glyph on the rows:
 *   the modal header already carries "Your TasteMatch™", and repeating the
 *   glyph on every row would cheapen it.
 * - The tier word is not stored here: it comes from `RATING_EMOJIS`, the same
 *   source as the header, so the key and the header always agree ("Highest",
 *   where the legacy app said "Most").
 * - Paragraphs: "pin colors" and "on the map" are dropped because this modal
 *   shows no map pins; "personal compatibility" became "personal TasteMatch".
 *
 * The no-personal-data state's public-rating paragraph (public opinion /
 * one-size-fits-all score, from the legacy gray-pin sentence) is shown only
 * to explain a public rating as just that, never as a TasteMatch (paire-app#1).
 */

/** A compatibility level, 0 (No) through 5 (Highest). */
export type CompatibilityLevel = 0 | 1 | 2 | 3 | 4 | 5;

export const compatibilityModalCopy = {
  /** Header title in the no-personal-data state (the tier name replaces it otherwise). */
  stillLearning: 'Still Learning',
  /** Header subtitle under the tier name / "Still Learning". */
  headerSubtitle: 'Your TasteMatch™',
  /** Heading above the six-tier key. */
  keyHeading: 'TasteMatch Key',
  /** Screen-reader-only marker appended to the member's current tier row. */
  currentTierMarker: '(your match)',
  /** Row label for a tier, e.g. "Highest Personal TasteMatch". */
  keyRowLabel: (tierWord: string) => `${tierWord} Personal TasteMatch`,
  /** Explanatory paragraphs shown below the key (personal-data state). */
  keyExplanation: [
    'Paire uses emojis of various expressions and corresponding colors to indicate your own personal TasteMatch with each restaurant.',
    'As you build your PersonalTaste™ Profile (by taking more quizzes and privately rating more restaurants), Paire gradually gets to know you better, so each restaurant’s emoji and color reflect your own personal TasteMatch more closely.',
  ],
  /** No-personal-data ("Still Learning") state: title above the body line. */
  noDataTitle: 'More PersonalTaste™ data needed',
  /** No-personal-data state: body line under the title. */
  noDataBody:
    'Remember: The accuracy of personal TasteMatches improves with every quiz taken and every restaurant privately rated.',
  /**
   * No-personal-data state, shown only when the restaurant has a public rating:
   * states the rating and what a public rating is (and is not).
   */
  publicRatingParagraph: (rating: number) =>
    `This restaurant has a public rating of ${rating.toFixed(1)} stars. A public rating reflects the average public opinion, which is a one-size-fits-all score from online rating & review platforms.`,
} as const;
